import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { LoadingButton } from '@mui/lab';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  useTheme,
  Typography,
  Switch,
  TextField,
  FormControlLabel,
  Stack,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { useGetSettingsByAgencyId } from '@frontend/features/agencies/queries';
import { useGetUsersByAgency } from '@frontend/features/users/queries';
import { SettingsFields } from '@bandido/shared/types';
import { UserListByTeamDto } from '@bandido/shared/dtos';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { MultiSelectItem } from '@frontend/features/agencies/components/Settings/components/types';

type SettingsForm = Record<SettingsFields, string | boolean | MultiSelectItem[]>;

export const IaConfigView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: settings } = useGetSettingsByAgencyId({ id });
  const { data: usersData } = useGetUsersByAgency({
    agencyId: id,
    pageSize: 10000,
    search: ''
  });
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListByTeamDto | null>(null);

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const [formData, setFormData] = useState({
    [SettingsFields.AI_CASE_HANDLERS_ENABLED]:
      settings?.[SettingsFields.AI_CASE_HANDLERS_ENABLED] === 'true' || false,
    [SettingsFields.IA_USER_ID]: settings?.[SettingsFields.IA_USER_ID] || '',
    [SettingsFields.WHATSAPP_OPENAI_API_KEY]:
      settings?.[SettingsFields.WHATSAPP_OPENAI_API_KEY] || '',
    [SettingsFields.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES]:
      settings?.[SettingsFields.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES] || '10',
  });

  const users = usersData?.items || [];

  React.useEffect(() => {
    if (settings) {
      setFormData({
        [SettingsFields.AI_CASE_HANDLERS_ENABLED]:
          settings[SettingsFields.AI_CASE_HANDLERS_ENABLED] === 'true' || false,
        [SettingsFields.IA_USER_ID]: settings[SettingsFields.IA_USER_ID] || '',
        [SettingsFields.WHATSAPP_OPENAI_API_KEY]:
          settings[SettingsFields.WHATSAPP_OPENAI_API_KEY] || '',
        [SettingsFields.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES]:
          settings[SettingsFields.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES] || '10',
      });

      if (settings[SettingsFields.IA_USER_ID] && users.length > 0) {
        const foundUser = users.find((user: UserListByTeamDto) => user.id === settings[SettingsFields.IA_USER_ID]);
        setSelectedUser(foundUser || null);
      }
    }
  }, [settings, users]);

  const handleSwitchChange = (field: SettingsFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleTextFieldChange = (field: SettingsFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleNumberFieldChange = (field: SettingsFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleUserChange = (user: UserListByTeamDto | null) => {
    setSelectedUser(user);
    setFormData(prev => ({
      ...prev,
      [SettingsFields.IA_USER_ID]: user?.id || ''
    }));
  };

  const handleSave = (settings: Partial<SettingsForm>) => async () => {
    const payload = {} as Record<SettingsFields, string>;

    (Object.keys(settings) as SettingsFields[]).forEach((key) => {
      const value = settings[key];
      if (Array.isArray(value)) {
        const items = value.map((item) => item.value);
        payload[key] = JSON.stringify(items);
      } else if (typeof value === 'boolean') {
        payload[key] = value.toString();
      } else {
        payload[key] = value || '';
      }
    });

    try {
      setIsSaving(true);
      await client.post(`agency/${id}/ia-config`, payload);

      queryClient.invalidateQueries({ queryKey: [QueryKeys.getIAConfigByAgencyId, id] });
      addToast({
        message: t('agencies.settings.saveSuccess'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('agencies.settings.saveError'),
        color: 'error',
      });
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
        padding: theme.spacing(1, 3),
      }}
    >
      <OverlayScrollbarsComponent
        style={{
          marginTop: theme.spacing(5),
          boxSizing: 'border-box',
          width: '100%',
          overflowY: 'hidden',
          height: 'calc(100vh - 160px)',
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
        defer
      >
        <Box>
          <Box display="flex" flexDirection="column" gap={4}>
            <Typography
              variant="h6"
              gutterBottom
            >
              {t('agencies.settings.ai')}
            </Typography>

            <Stack spacing={4}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData[SettingsFields.AI_CASE_HANDLERS_ENABLED]}
                      onChange={handleSwitchChange(SettingsFields.AI_CASE_HANDLERS_ENABLED)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {t('agencies.settings.envMap.AI_CASE_HANDLERS_ENABLED')}
                    </Typography>
                  }
                />
              </Box>

              <Autocomplete
                size="small"
                fullWidth
                options={users}
                value={selectedUser}
                onChange={(_event, newValue) => handleUserChange(newValue)}
                getOptionLabel={(option) =>
                  `${option.firstName || ''} ${option.lastName || ''} (${option.email})`.trim()
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(`agencies.settings.envMap.${SettingsFields.IA_USER_ID}`)}
                    placeholder={t('agencies.settings.placeholders.selectAIUser')}
                    variant="outlined"
                  />
                )}
                noOptionsText={t('agencies.settings.placeholders.noUsersFound')}
                loading={!usersData}
              />

              <TextField
                size='small'
                fullWidth
                label={t('agencies.settings.envMap.WHATSAPP_OPENAI_API_KEY')}
                value={formData[SettingsFields.WHATSAPP_OPENAI_API_KEY]}
                onChange={handleTextFieldChange(SettingsFields.WHATSAPP_OPENAI_API_KEY)}
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={handleShowPassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                size='small'
                fullWidth
                label={t('agencies.settings.envMap.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES')}
                value={formData[SettingsFields.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES]}
                onChange={handleNumberFieldChange(SettingsFields.WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES)}
                variant="outlined"
                type="number"
                inputProps={{ min: 1, max: 100 }}
                placeholder={t('agencies.settings.placeholders.contextMessages')}
              />
            </Stack>

            <Box sx={{ marginTop: theme.spacing(4), display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton loading={isSaving} type="submit" variant="contained" color="primary" onClick={handleSave(formData)}>
                {t('common.save')}
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      </OverlayScrollbarsComponent>
    </Box>
  );
};
